import {
  agreeConfirmationOptions,
  confirmationOptions,
  defaultFieldProps,
  facilityByOrganizationOptions,
  fullFieldProps,
  unitOptions,
} from 'src/constants';
import { FormFieldType, TGroupedFormDef } from 'src/types';
import { getAddressFields, positiveWholeNumber, positiveNumber } from 'src/utils';

export const nameAndTitleInstance = [
  {
    path: 'name',
    label: 'Name',
    required: true,
    type: FormFieldType.TEXT,
    lg: 3,
    xs: 3,
  },
  {
    path: 'title',
    label: 'Title',
    required: true,
    type: FormFieldType.TEXT,
    lg: 9,
    xs: 9,
  },
];

export const testingPlanForms: TGroupedFormDef = {
  title: 'Testing Plan form',
  groups: [
    {
      groupName: 'Facilities',
      showGroupName: true,
      fields: [
        {
          label: 'Is your facility owned or leased by your organization?',
          path: 'facilityByOrganization',
          type: FormFieldType.RADIO_GROUP,
          options: facilityByOrganizationOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'How long has your organization been located at this location?',
          path: 'organizationLocatedDuration',
          maxLength: 500,
          type: FormFieldType.TEXT,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Is there a known date planned for your organization to relocate?',
          path: 'hasOrganizationRelocationPlanned',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'If yes: When?',
          path: 'organizationRelocationPlannedDate',
          type: FormFieldType.DATE,
          required: true,
          ...defaultFieldProps,
          renderCondition: (field) => field.hasOrganizationRelocationPlanned === 'yes',
        },
        {
          label: 'Please add details regarding your testing rooms.',
          path: 'testingRoomDetails',
          type: FormFieldType.MULTI_INSTANCE,
          maxEntries: 10,
          required: true,
          comment: 'Please note: Testing rooms must have sufficient power and WIFI access',
          addButton: 'Add Testing Room',
          deleteButton: 'Delete Testing Room',
          properties: [
            {
              path: 'roomType',
              label: 'Room Type',
              required: true,
              type: FormFieldType.TEXT,
              lg: 9,
              xs: 9,
            },
            {
              path: 'maximumCapacity',
              label: 'Maximum Capacity',
              required: true,
              type: FormFieldType.NUMBER,
              lg: 3,
              xs: 3,
              validator: {
                validate: positiveWholeNumber,
                validationMessage: 'Must be a positive whole number.'
              }
            },
          ],
          ...fullFieldProps,
        },
        {
          label: 'What type of tables will be used for testing?',
          path: 'testingTableTypes',
          type: FormFieldType.TEXT,
          maxLength: 500,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Please confirm students will be seated facing the same direction.',
          path: 'studentsSeatedSameDirection',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Please confirm students will be spaced in accordance with assessment specific spacing policy.',
          path: 'studentsSpacedAccordingly',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          label:
            'Please confirm students will be seated randomly (not alphabetically or in another predictable pattern)',
          path: 'studentsSeatedRandomly',
          type: FormFieldType.RADIO_GROUP,
          options: confirmationOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          acceptFileTypes: 'image/*',
          label: 'Please upload a picture of a testing room set up for testing.',
          path: 'testingRoomPicture',
          type: FormFieldType.LOCAL_FILE_UPLOAD,
          uploadButton: 'Add picture',
          required: true,
          ...fullFieldProps,
        },
      ],
    },
    {
      groupName: 'Exam Administration Staff and Training',
      showGroupName: true,
      fields: [
        {
          label: 'Name and title of individuals responsible for providing test administration training',
          path: 'testAdministrationTrainingIndividuals',
          addButton: 'Add individual',
          deleteButton: 'Delete individual',
          type: FormFieldType.MULTI_INSTANCE,
          maxEntries: 5,
          required: true,
          properties: nameAndTitleInstance,
          ...fullFieldProps,
        },
        {
          label: 'Where and when will training be provided?',
          path: 'trainingProvisionDetails',
          type: FormFieldType.TEXT,
          maxLength: 500,
          required: true,
          ...fullFieldProps,
        },
        {
          label:
            'Confirm training will be in accordance with the assessment specific coordinator manual (IE: SAT Coordinator manual for SAT exam administration)',
          path: 'trainingConfirmationAccordinglyWithAssessment',
          type: FormFieldType.RADIO_GROUP,
          options: agreeConfirmationOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          label:
            'Confirm sufficient and continued communication will be sent to alert all staff and parents of test day, and to ensure no distractions from other students, announcements, bells, fire drills, etc.',
          path: 'continuedCommunicationConfirmationToAlert',
          type: FormFieldType.RADIO_GROUP,
          options: agreeConfirmationOptions,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Regarding your test day staffing plan; How many testing room staff members will be scheduled?',
          path: 'testingRoomStaffMembersCount',
          type: FormFieldType.NUMBER,
          required: true,
          ...fullFieldProps,
          validator: {
            validate: positiveWholeNumber,
            validationMessage: 'Must be a positive whole number.'
          }
        },
        {
          label: 'How many non-testing room staff members will be scheduled?',
          path: 'nonTestingRoomStaffMembersCount',
          type: FormFieldType.NUMBER,
          required: true,
          ...fullFieldProps,
          validator: {
            validate: positiveWholeNumber,
            validationMessage: 'Must be a positive whole number.'
          }
        },
        {
          label: 'Please provide your planned test day break schedule.',
          path: 'plannedTestDayBreakSchedule',
          type: FormFieldType.TEXT,
          maxLength: 500,
          multiple: true,
          required: true,
          ...fullFieldProps,
        },
      ],
    },
    {
      groupName: 'Secure Exam Material Storage',
      showGroupName: true,
      fields: [],
    },
    {
      groupName: 'Confirm the shipping address for your institution',
      showGroupName: true,
      fields: getAddressFields('institutionShippingAddress'),
    },
    {
      groupName: 'Secure Exam Material Storage Details',
      fields: [
        {
          acceptFileTypes: 'image/*',
          label: 'Please upload a picture of your facility from the outside',
          path: 'outsideFacilityPicture',
          type: FormFieldType.LOCAL_FILE_UPLOAD,
          uploadButton: 'Add picture',
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Name and title of individual responsible for receiving and storing secure materials',
          path: 'secureMaterialsManageIndividualDetails',
          type: FormFieldType.SINGLE_INSTANCE,
          required: true,
          properties: nameAndTitleInstance,
          ...fullFieldProps,
        },
        {
          label: 'Where and how will materials be stored?',
          path: 'howToStoreMaterials',
          type: FormFieldType.TEXT,
          maxLength: 500,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Names and titles of individuals with access to secure storage',
          path: 'secureMaterialsAccessIndividualDetails',
          addButton: 'Add individual',
          deleteButton: 'Delete individual',
          maxEntries: 5,
          type: FormFieldType.MULTI_INSTANCE,
          required: true,
          properties: nameAndTitleInstance,
          ...fullFieldProps,
        },
        {
          label: 'Number of keys to secure material storage area (if applicable)',
          path: 'secureMaterialsStorageKeysCount',
          type: FormFieldType.NUMBER,
          required: false,
          ...fullFieldProps,
          validator: {
            validate: positiveWholeNumber,
            validationMessage: 'Must be a positive whole number.'
          }
        },
        {
          acceptFileTypes: 'image/*',
          label: 'Please upload a picture of storage area',
          path: 'storageAreaPicture',
          type: FormFieldType.LOCAL_FILE_UPLOAD,
          uploadButton: 'Add picture',
          required: true,
          ...fullFieldProps,
        },
      ],
    },
    {
      groupName: 'Exam Administration Material Distribution',
      showGroupName: true,
      fields: [
        {
          label: 'Name and title of test supervisor?',
          path: 'testSupervisorDetail',
          type: FormFieldType.SINGLE_INSTANCE,
          required: true,
          properties: nameAndTitleInstance,
          ...fullFieldProps,
        },
        {
          label: 'Describe how testing materials will be prepared for separate testing rooms.',
          path: 'testingMaterialsPreparationDescription',
          maxLength: 500,
          type: FormFieldType.TEXT,
          multiple: true,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Approximately how far away are the testing rooms from the secure exam material storage area?',
          path: 'distanceBetweenTestingRoomAndStorageArea',
          type: FormFieldType.SINGLE_INSTANCE,
          required: true,
          properties: [
            {
              path: 'distance',
              label: 'Distance',
              required: true,
              type: FormFieldType.NUMBER,
              lg: 3,
              xs: 3,
              step: 'any',
              validator: {
                validate: positiveNumber,
                validationMessage: 'Must be a positive number.'
              }
            },
            {
              path: 'unit',
              label: 'Unit',
              required: true,
              type: FormFieldType.SELECT,
              placeholder: 'Select one',
              options: unitOptions,
              lg: 9,
              xs: 9,
            },
          ],
          ...fullFieldProps,
        },
        {
          label: 'How will exams be transported to testing rooms?',
          path: 'testingRoomsTransportation',
          maxLength: 2000,
          type: FormFieldType.TEXT,
          multiple: true,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'Name and title of individual responsible for exam material distribution to testing rooms',
          path: 'testingRoomExamMaterialDistributionIndividuals',
          type: FormFieldType.MULTI_INSTANCE,
          maxEntries: 5,
          required: true,
          properties: nameAndTitleInstance,
          addButton: 'Add individual',
          deleteButton: 'Delete individual',
          ...fullFieldProps,
        },
        {
          label:
            'Confirm test center supervisor will ensure proper handling, accounting and distribution of materials and adherence to test day procedures.',
          path: 'testCenterSupervisiorEnsureConfirmation',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          ...fullFieldProps,
        },
      ],
    },
    {
      groupName: 'Exam Administration Security',
      showGroupName: true,
      fields: [
        {
          label:
            'Confirm your organization will adhere to outlined policies regarding testing and security violations.',
          path: 'organizationAdhereOutlinedConfirmation',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          ...fullFieldProps,
        },
        {
          label: 'Confirm outside students will be verified with acceptable ID.',
          path: 'outsideStudentsVerificationConfirmation',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          ...fullFieldProps,
        },
        {
          label: 'If yes: Please list accepted forms of ID.',
          path: 'listAcceptedIDForms',
          type: FormFieldType.TEXT,
          maxLength: 500,
          required: true,
          ...fullFieldProps,
          renderCondition: (field) => field.outsideStudentsVerificationConfirmation === 'yes',
        },
        {
          label: 'Explain how will late arrivals be handled?',
          path: 'lateArrivalsHandleDetails',
          type: FormFieldType.TEXT,
          maxLength: 500,
          multiple: true,
          required: true,
          ...fullFieldProps,
        },
        {
          label: 'How will your facility handle test takers over 21 years of age? ',
          path: 'facilityHandleTestTakersDetails',
          type: FormFieldType.TEXT,
          maxLength: 500,
          required: true,
          ...fullFieldProps,
        },
      ],
    },
    {
      groupName: 'College Board Approved Accommodations',
      showGroupName: true,
      fields: [
        {
          label: 'Name & Title of coordinator for testing with accommodations',
          path: 'accomodationTestingCoordinatorDetails',
          type: FormFieldType.SINGLE_INSTANCE,
          required: true,
          properties: nameAndTitleInstance,
          ...fullFieldProps,
        },
        {
          label: 'Will your facility have separate testing rooms available for students with accommodations?',
          path: 'accomodationSeparateTestingRoomsConfirmation',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          ...fullFieldProps,
        },
        {
          label: 'Will your facility remain available for extended time? (IE: testing over two days)',
          path: 'extendedTimeFacilityRemainConfirmation',
          type: FormFieldType.RADIO_GROUP,
          required: true,
          options: confirmationOptions,
          ...fullFieldProps,
        },
      ],
    },
    {
      groupName: 'Post Administration Tasks',
      showGroupName: true,
      fields: [
        {
          label:
            'Name & title of individual responsible for accounting for, inventorying and returning test materials ',
          path: 'testingMaterialsAccountingIndividualDetails',
          type: FormFieldType.SINGLE_INSTANCE,
          required: true,
          properties: nameAndTitleInstance,
          ...fullFieldProps,
        },
        {
          label: 'Where will secure materials be secured until returned?',
          path: 'secureMaterialsReturnDate',
          type: FormFieldType.TEXT,
          maxLength: 500,
          required: true,
          ...fullFieldProps,
        },
      ],
    },
  ],
};
